// src/pages/AnimalForm.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Paper,
  Typography,
  FormHelperText,
  Autocomplete,
  CircularProgress,
  Divider,
  Alert,
  Breadcrumbs,
  Link,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useNavigate, useSearchParams, Link as RouterLink } from "react-router-dom";
import { createAnimal, getAnimalFormOptions, searchDams, searchSires } from "../../api/animal";
import { useSnackbar } from "notistack";
import useAuth from "../../hooks/useAuth";
import { APP_HOME_PATH } from "../../config/routes";

// Options
const GENDER_OPTIONS = ["Male", "Female"];
const CATEGORY_OPTIONS = ["Heifer", "Steer", "Bull", "Cow", "Calf"];
const STATUS_OPTIONS = ["On Farm", "Sold", "Deceased", "Transferred"];

const MIN_QUERY = 1;

const Section = ({ title, children }) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
      {title}
    </Typography>
    <Divider sx={{ mb: 2 }} />
    {children}
  </Paper>
);

export default function AnimalForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const isSuperAdmin = user?.roles?.some(
    (r) => r === "super_admin" || r?.name === "super_admin"
  );
  const rawTenantId = searchParams.get("tenant_id");
  const tidNum =
    rawTenantId != null && rawTenantId !== ""
      ? Number(rawTenantId)
      : null;
  const farmTenantId =
    tidNum != null && Number.isFinite(tidNum) ? tidNum : null;
  const animalsListQs =
    farmTenantId != null ? `?tenant_id=${farmTenantId}` : "";
  const apiFarmTenant = isSuperAdmin ? farmTenantId : null;

  // Parent search state
  const [damQuery, setDamQuery] = useState("");
  const [damOptions, setDamOptions] = useState([]);
  const [damLoading, setDamLoading] = useState(false);

  const [sireQuery, setSireQuery] = useState("");
  const [sireOptions, setSireOptions] = useState([]);
  const [sireLoading, setSireLoading] = useState(false);

  const [kraalOptions, setKraalOptions] = useState([]);
  const [breedOptions, setBreedOptions] = useState([]);
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [damSelected, setDamSelected] = useState(null);
  const [sireSelected, setSireSelected] = useState(null);

  const formik = useFormik({
    initialValues: {
      tag_number: "",
      alias: "",
      pattern_markings: "",
      breed: "",
      description: "",
      dob: null,
      gender: "",
      category: "",
      status: "On Farm",
      weight: "",
      is_active: true,
      dam_id: null,
      sire_id: null,
      kraal_name: "",
      tags: [],
    },
    validationSchema: Yup.object({
      tag_number: Yup.string().required("Tag number is required"),
      breed: Yup.string()
        .max(50, "Max 50 characters")
        .required("Breed is required"),
      dob: Yup.date()
        .nullable()
        .transform((value, originalValue) =>
          originalValue === "" ? null : value
        )
        .max(new Date(), "Date cannot be in the future"),
      gender: Yup.string().required("Gender is required"),
      category: Yup.string()
        .required("Category is required")
        .test(
          "matches-gender",
          "Bull and Steer are male; Cow and Heifer are female. Choose a category that matches gender (Calf can be either).",
          function categoryMatchesGender(val) {
            const gender = this.parent.gender;
            if (!val || !gender) return true;
            const g = String(gender).toLowerCase();
            const c = String(val).toLowerCase();
            if (g === "female" && (c === "bull" || c === "steer")) return false;
            if (g === "male" && (c === "cow" || c === "heifer")) return false;
            return true;
          }
        ),
      status: Yup.string().required("Status is required"),
      weight: Yup.number()
        .transform((value, originalValue) =>
          originalValue === "" || originalValue == null ? undefined : value
        )
        .nullable()
        .integer("Use whole kilograms")
        .min(0, "Weight must be positive")
        .optional(),
      kraal_name: Yup.string().max(50, "Max 50 characters").optional(),
      tags: Yup.array().of(Yup.string()),
    }),
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      try {
        const formatted = {
          ...values,
          dob: values.dob
            ? values.dob.toISOString
              ? values.dob.toISOString()
              : values.dob
            : null,
          dam_id: values.dam_id ?? null,
          sire_id: values.sire_id ?? null,
        };

        if (formatted.weight === "" || formatted.weight == null) {
          delete formatted.weight;
        } else {
          formatted.weight = Number(formatted.weight);
        }
        const kn = (formatted.kraal_name || "").trim();
        if (!kn) delete formatted.kraal_name;
        else formatted.kraal_name = kn;

        const br = (formatted.breed || "").trim();
        formatted.breed = br;

        if (!formatted.tags?.length) delete formatted.tags;

        // Optionally prune nulls so they’re omitted entirely:
        ["dob", "dam_id", "sire_id"].forEach((k) => {
          if (formatted[k] == null) delete formatted[k];
        });

        if (isSuperAdmin && farmTenantId == null) {
          enqueueSnackbar(
            "Platform admins must open this page from the animal list (with an organization selected).",
            { variant: "error" }
          );
          return;
        }
        const res = await createAnimal(formatted, apiFarmTenant);
        enqueueSnackbar("Animal created successfully!", { variant: "success" });
        const profileQs =
          isSuperAdmin && farmTenantId != null
            ? `?tenant_id=${farmTenantId}`
            : "";
        navigate(`/animal/${res.id}${profileQs}`);
      } catch (error) {
        if (error?.response?.data?.errors) {
          const serverErrors = {};
          Object.keys(error.response.data.errors).forEach((k) => {
            serverErrors[k] = error.response.data.errors[k][0];
          });
          setErrors(serverErrors);
        }
        enqueueSnackbar(
          error?.response?.data?.message || "Failed to create animal",
          { variant: "error" }
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Search: Dams
  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!damQuery || damQuery.trim().length < MIN_QUERY) {
        if (active) setDamOptions([]);
        return;
      }
      setDamLoading(true);
      try {
        const rows = await searchDams(damQuery.trim(), apiFarmTenant);
        if (active) setDamOptions(rows);
      } finally {
        if (active) setDamLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [damQuery, apiFarmTenant]);

  // Search: Sires
  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!sireQuery || sireQuery.trim().length < MIN_QUERY) {
        if (active) setSireOptions([]);
        return;
      }
      setSireLoading(true);
      try {
        const rows = await searchSires(sireQuery.trim(), apiFarmTenant);
        if (active) setSireOptions(rows);
      } finally {
        if (active) setSireLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [sireQuery, apiFarmTenant]);

  useEffect(() => {
    if (isSuperAdmin && farmTenantId == null) return;
    let active = true;
    (async () => {
      const opts = await getAnimalFormOptions(apiFarmTenant);
      if (!active) return;
      setKraalOptions(opts.kraal_names);
      setBreedOptions(opts.breed_suggestions);
      setTagSuggestions(opts.tag_suggestions);
    })();
    return () => {
      active = false;
    };
  }, [isSuperAdmin, farmTenantId, apiFarmTenant]);

  const getParentLabel = (opt) =>
    opt ? `${opt.tag_number}${opt.alias ? ` • ${opt.alias}` : ""}` : "";

  const damOptionsMerged = useMemo(() => {
    const byId = new Map(damOptions.map((o) => [o.id, o]));
    if (damSelected?.id != null) byId.set(damSelected.id, damSelected);
    return Array.from(byId.values());
  }, [damOptions, damSelected]);

  const sireOptionsMerged = useMemo(() => {
    const byId = new Map(sireOptions.map((o) => [o.id, o]));
    if (sireSelected?.id != null) byId.set(sireSelected.id, sireSelected);
    return Array.from(byId.values());
  }, [sireOptions, sireSelected]);

  if (isSuperAdmin && farmTenantId == null) {
    return (
      <Box sx={{ p: 3, maxWidth: 560 }}>
        <Breadcrumbs sx={{ mb: 2 }} aria-label="breadcrumb">
          <Link component={RouterLink} underline="hover" color="inherit" to={APP_HOME_PATH}>
            Dashboard
          </Link>
          <Link component={RouterLink} underline="hover" color="inherit" to="/animals">
            Animals
          </Link>
          <Typography color="text.primary">Add new animal</Typography>
        </Breadcrumbs>
        <Alert severity="info" sx={{ mb: 2 }}>
          As a platform admin, choose an organization on the{" "}
          <strong>Animal list</strong>, then use <strong>Add New Animal</strong>{" "}
          so the correct farm is selected. You can also append{" "}
          <code>?tenant_id=…</code> to this URL.
        </Alert>
        <Button variant="contained" component={RouterLink} to="/animals">
          Go to animal list
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ maxWidth: 900, mx: "auto", mb: 2 }}>
        <Breadcrumbs
          aria-label="breadcrumb"
          sx={{ flexWrap: "wrap" }}
        >
          <Link component={RouterLink} underline="hover" color="inherit" to={APP_HOME_PATH}>
            Dashboard
          </Link>
          <Link
            component={RouterLink}
            underline="hover"
            color="inherit"
            to={`/animals${animalsListQs}`}
          >
            Animals
          </Link>
          <Typography color="text.primary">Add new animal</Typography>
        </Breadcrumbs>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: 1.5,
          mb: 2,
          maxWidth: 900,
          mx: "auto",
        }}
      >
        <Typography
          component="h1"
          sx={{ fontWeight: 700, fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
        >
          Add New Animal
        </Typography>
        <Button
          variant="outlined"
          onClick={() => navigate(`/animals${animalsListQs}`)}
          sx={{ width: { xs: "100%", sm: "auto" }, flexShrink: 0 }}
        >
          Back to Animals
        </Button>
      </Box>

      <Box sx={{ display: "grid", gap: 2, maxWidth: 900, mx: "auto" }}>
        {/* Basic Information (stacked) */}
        <Section title="Basic Information">
          <Grid container spacing={2} direction="column">
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                id="tag_number"
                name="tag_number"
                label="Tag Number *"
                value={formik.values.tag_number}
                onChange={formik.handleChange}
                error={
                  formik.touched.tag_number && Boolean(formik.errors.tag_number)
                }
                helperText={
                  formik.touched.tag_number && formik.errors.tag_number
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                id="alias"
                name="alias"
                label="Name/Alias"
                value={formik.values.alias}
                onChange={formik.handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                size="small"
                freeSolo
                options={breedOptions}
                value={formik.values.breed}
                onChange={(_, newValue) => {
                  formik.setFieldValue("breed", newValue ?? "");
                }}
                onInputChange={(_, newInputValue, reason) => {
                  if (reason === "input" || reason === "clear") {
                    formik.setFieldValue("breed", newInputValue ?? "");
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Breed *"
                    error={formik.touched.breed && Boolean(formik.errors.breed)}
                    helperText={
                      formik.touched.breed && formik.errors.breed
                        ? formik.errors.breed
                        : "Pick a breed already used on this farm or type a new one."
                    }
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <DatePicker
                label="Date of birth (optional)"
                value={formik.values.dob}
                onChange={(date) => formik.setFieldValue("dob", date)}
                maxDate={new Date()}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    fullWidth
                    error={formik.touched.dob && Boolean(formik.errors.dob)}
                    helperText={formik.touched.dob && formik.errors.dob}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                id="description"
                name="description"
                label="Description"
                value={formik.values.description}
                onChange={formik.handleChange}
                multiline
                rows={3}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                id="pattern_markings"
                name="pattern_markings"
                label="Pattern/Markings"
                value={formik.values.pattern_markings}
                onChange={formik.handleChange}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </Section>

        {/* Characteristics (stacked) */}
        <Section title="Characteristics">
          <Grid container spacing={2} direction="column">
            <Grid item xs={12}>
              <FormControl
                fullWidth
                error={formik.touched.gender && Boolean(formik.errors.gender)}
              >
                <InputLabel size="small">Gender *</InputLabel>
                <Select
                  id="gender"
                  name="gender"
                  label="Gender *"
                  size="small"
                  value={formik.values.gender}
                  onChange={formik.handleChange}
                >
                  {GENDER_OPTIONS.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {formik.touched.gender && formik.errors.gender}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl
                fullWidth
                error={
                  formik.touched.category && Boolean(formik.errors.category)
                }
              >
                <InputLabel size="small">Category *</InputLabel>
                <Select
                  id="category"
                  name="category"
                  label="Category *"
                  size="small"
                  value={formik.values.category}
                  onChange={formik.handleChange}
                >
                  {CATEGORY_OPTIONS.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {formik.touched.category && formik.errors.category}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl
                fullWidth
                error={formik.touched.status && Boolean(formik.errors.status)}
              >
                <InputLabel size="small">Status *</InputLabel>
                <Select
                  id="status"
                  name="status"
                  label="Status *"
                  size="small"
                  value={formik.values.status}
                  onChange={formik.handleChange}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {formik.touched.status && formik.errors.status}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                id="weight"
                name="weight"
                label="Weight (kg)"
                type="number"
                value={formik.values.weight}
                onChange={formik.handleChange}
                error={formik.touched.weight && Boolean(formik.errors.weight)}
                helperText={
                  formik.touched.weight && formik.errors.weight
                    ? formik.errors.weight
                    : "Optional — add when known (e.g. after weighing)."
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                size="small"
                options={kraalOptions}
                value={formik.values.kraal_name}
                onChange={(_, newValue) => {
                  formik.setFieldValue("kraal_name", newValue ?? "");
                }}
                onInputChange={(_, newInputValue, reason) => {
                  if (reason === "input" || reason === "clear") {
                    formik.setFieldValue("kraal_name", newInputValue ?? "");
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id="kraal_name"
                    name="kraal_name"
                    label="Kraal / pen"
                    error={
                      formik.touched.kraal_name &&
                      Boolean(formik.errors.kraal_name)
                    }
                    helperText={
                      formik.touched.kraal_name && formik.errors.kraal_name
                        ? formik.errors.kraal_name
                        : "Pick a kraal already used on this farm or type a new name."
                    }
                  />
                )}
              />
            </Grid>
          </Grid>
        </Section>

        {/* Tags (stacked + wide) */}
        <Section title="Tags">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Suggestions come from tags already used on animals in your
            organization. You can still add new tags.
          </Typography>
          <Autocomplete
            multiple
            freeSolo
            options={tagSuggestions}
            value={formik.values.tags}
            onChange={(_, newValue) => formik.setFieldValue("tags", newValue)}
            renderTags={(value, getTagProps) =>
              value.map((opt, i) => (
                <Chip key={i} label={opt} {...getTagProps({ index: i })} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                fullWidth
                label="Add tags"
                placeholder="Type a tag and press Enter"
              />
            )}
            sx={{ width: "100%" }}
          />
        </Section>

        {/* Parent Information (stacked) */}
        <Section title="Parent information (optional)">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Search females (dam) or males (sire) by tag or alias. Results load
            as you type; your selection stays even if you clear the search box.
          </Typography>
          <Grid container spacing={2} direction="column">
            <Grid item xs={12}>
              <Autocomplete
                size="small"
                value={damSelected}
                options={damOptionsMerged}
                loading={damLoading}
                onChange={(_, val) => {
                  formik.setFieldValue("dam_id", val?.id ?? null);
                  setDamSelected(val ?? null);
                }}
                onInputChange={(_, val, reason) => {
                  if (reason === "input") setDamQuery(val);
                }}
                filterOptions={(x) => x}
                getOptionLabel={getParentLabel}
                isOptionEqualToValue={(o, v) => o?.id === v?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Dam (mother)"
                    placeholder="Search by tag or name…"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {damLoading ? <CircularProgress size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                size="small"
                value={sireSelected}
                options={sireOptionsMerged}
                loading={sireLoading}
                onChange={(_, val) => {
                  formik.setFieldValue("sire_id", val?.id ?? null);
                  setSireSelected(val ?? null);
                }}
                onInputChange={(_, val, reason) => {
                  if (reason === "input") setSireQuery(val);
                }}
                filterOptions={(x) => x}
                getOptionLabel={getParentLabel}
                isOptionEqualToValue={(o, v) => o?.id === v?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Sire (father)"
                    placeholder="Search by tag or name…"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {sireLoading ? <CircularProgress size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Section>

        {/* Actions */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column-reverse", sm: "row" },
            justifyContent: "flex-end",
            gap: 1.5,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => navigate(`/animals${animalsListQs}`)}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            onClick={formik.handleSubmit}
            disabled={formik.isSubmitting}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            {formik.isSubmitting ? "Saving…" : "Save Animal"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
